import {
  Divider,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

/** Toggle label for grouped facilitator timeline (was「共識時間軸」). */
export const INTEGRATED_RESPONSES_MODE_LABEL = '整合全體回覆';

/** Participant published-official view: group own marks with official on the timeline. */
export const INTEGRATE_OFFICIAL_MARKS_LABEL = '整合官方判決';

const INTEGRATED_RESPONSES_HELP =
  '開啟後判決時間軸改為整合全體參與者回覆（含 % 共識），並可查看事件統計；管理員視角一致。關閉時只顯示您自己的標記。';
const EDUCATION_MODE_HELP =
  '須先開啟「整合全體回覆」。開啟後隱藏官方／檢視答案，適合課堂或現場教學；建立者開啟時無法新增官方標記。';
const EDUCATION_MODE_DISABLED_HELP = '請先開啟「整合全體回覆」才可使用教學模式。';

const INTEGRATE_OFFICIAL_MARKS_HELP =
  '開啟後判決時間軸按時間整合顯示官方判決與您的標記（含 ✓／群組圖示）；關閉時只顯示您的標記。';

interface ToggleProps {
  label: string;
  help: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  tone?: 'default' | 'dark';
}

function ViewModeToggle({ label, help, checked, disabled = false, onChange, tone = 'default' }: ToggleProps) {
  const isDarkTone = tone === 'dark';
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{ px: 0.75, opacity: disabled ? 0.45 : 1 }}
    >
      <Switch
        size="small"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        color="secondary"
        inputProps={{ 'aria-label': label }}
      />
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{
          whiteSpace: 'nowrap',
          userSelect: 'none',
          color: isDarkTone ? 'rgba(255,255,255,0.92)' : 'text.primary',
        }}
      >
        {label}
      </Typography>
      <Tooltip title={help} arrow placement="top" enterTouchDelay={0}>
        <IconButton
          size="small"
          aria-label={`${label}說明`}
          sx={{
            p: 0.25,
            flexShrink: 0,
            color: isDarkTone ? 'rgba(255,255,255,0.82)' : 'text.secondary',
            '&:hover': {
              color: isDarkTone ? '#fff' : 'text.primary',
              bgcolor: 'action.selected',
            },
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

export interface ReviewViewModeToolbarProps {
  canUseConsensusTimeline?: boolean;
  consensusTimeline?: boolean;
  onConsensusTimelineChange?: (enabled: boolean) => void;
  canManageEducationMode?: boolean;
  educationMode?: boolean;
  onEducationModeChange?: (enabled: boolean) => void;
  /** 已發佈官方判決 — participant comparison timeline */
  canIntegrateOfficialMarks?: boolean;
  integrateOfficialMarks?: boolean;
  onIntegrateOfficialMarksChange?: (enabled: boolean) => void;
  /** Pro mode header — match contrast on dark toolbar strip. */
  tone?: 'default' | 'dark';
}

export default function ReviewViewModeToolbar({
  canUseConsensusTimeline = false,
  consensusTimeline = false,
  onConsensusTimelineChange,
  canManageEducationMode = false,
  educationMode = false,
  onEducationModeChange,
  canIntegrateOfficialMarks = false,
  integrateOfficialMarks = false,
  onIntegrateOfficialMarksChange,
  tone = 'default',
}: ReviewViewModeToolbarProps) {
  if (!canUseConsensusTimeline && !canManageEducationMode && !canIntegrateOfficialMarks) return null;

  const isDarkTone = tone === 'dark';
  const showDividerAfterIntegrate =
    canIntegrateOfficialMarks && (canUseConsensusTimeline || canManageEducationMode);
  const showDividerBeforeEducation =
    canUseConsensusTimeline && canManageEducationMode;

  return (
    <Stack
      direction="row"
      alignItems="center"
      divider={
        showDividerAfterIntegrate || showDividerBeforeEducation ? (
          <Divider
            orientation="vertical"
            flexItem
            sx={{ my: 0.75, borderColor: isDarkTone ? 'rgba(255,255,255,0.12)' : 'divider' }}
          />
        ) : undefined
      }
      sx={{
        flexShrink: 0,
        py: 0.25,
        px: 0.5,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: isDarkTone ? 'rgba(255,255,255,0.12)' : 'divider',
        bgcolor: isDarkTone
          ? 'rgba(255,255,255,0.04)'
          : (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'),
        boxShadow: isDarkTone ? 'none' : (theme) => (theme.palette.mode === 'dark' ? 'none' : '0 1px 2px rgba(0,0,0,0.04)'),
      }}
    >
      {canIntegrateOfficialMarks && (
        <ViewModeToggle
          label={INTEGRATE_OFFICIAL_MARKS_LABEL}
          help={INTEGRATE_OFFICIAL_MARKS_HELP}
          checked={integrateOfficialMarks}
          tone={tone}
          onChange={(v) => onIntegrateOfficialMarksChange?.(v)}
        />
      )}
      {canUseConsensusTimeline && (
        <ViewModeToggle
          label={INTEGRATED_RESPONSES_MODE_LABEL}
          help={INTEGRATED_RESPONSES_HELP}
          checked={consensusTimeline}
          tone={tone}
          onChange={(v) => {
            onConsensusTimelineChange?.(v);
            if (!v) onEducationModeChange?.(false);
          }}
        />
      )}
      {canManageEducationMode && (
        <ViewModeToggle
          label="教學模式"
          help={consensusTimeline ? EDUCATION_MODE_HELP : EDUCATION_MODE_DISABLED_HELP}
          checked={educationMode && consensusTimeline}
          disabled={!consensusTimeline}
          tone={tone}
          onChange={(v) => {
            if (!consensusTimeline) return;
            onEducationModeChange?.(v);
          }}
        />
      )}
    </Stack>
  );
}
